import { DOCUMENT } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    InputSignal,
    InputSignalWithTransform,
    OnDestroy,
    Signal,
    WritableSignal,
    afterNextRender,
    booleanAttribute,
    effect,
    inject,
    input,
    signal,
    viewChild,
} from '@angular/core';

import { BlockDirective, ElemDirective, ModDirective } from '@rt-tools/core';
import { RtButtonDirective } from '@rt-tools/ui-kit-v2';
import { IStairCalculationResult, IStairConfig } from '@stair-craft/common/util';

import {
    EPaneRole,
    IPane,
    IPaneRect,
    ISheet,
    ISheetFit,
    ITextMeasurer,
    SHEET_SIZE,
    SHEET_TITLE_BAND,
    SHEET_TITLE_FONT,
    createCanvasTextMeasurer,
    drawPlanPrimitives,
    elevationsSheet,
    fitSheet,
    planSheet,
} from './plan-projection';

interface IResolvedColors {
    paper: string;
    paper2: string;
    paper3: string;
    rule: string;
    ink: string;
    ink2: string;
    ink3: string;
    ink4: string;
    ink5: string;
    warn: string;
    err: string;
    step: string;
    stepStroke: string;
    winder: string;
    landing: string;
    opening: string;
    stairWidth: string;
    handrail: string;
    post: string;
    baluster: string;
    axis: string;
}

/** The laid-out sheet and what it was laid out for — the canvas size is not part of it. */
interface ISheetCacheEntry {
    result: IStairCalculationResult;
    config: IStairConfig;
    side: boolean;
    sheet: ISheet;
}

/** Space kept between the canvas controls and the fitted sheet, pixels. */
const CONTROLS_CLEARANCE: number = 6;
/** The PNG export draws the fixed sheet at this many image pixels per sheet pixel: 3200 × 2400. */
const EXPORT_PIXEL_RATIO: number = 2;
/** The user zoom goes from the fit (1) up to this many times it. */
const MAX_VIEW_ZOOM: number = 8;

const PANE_GRID_PX: number = 40;
/** Space between a view title and its scale, pixels. */
const TITLE_SCALE_GAP: number = 12;

const PAN_STEP_PX: number = 40;
const PAN_HOLD_STEP_PX: number = 6;
const ZOOM_STEP: number = 1.18;
const ZOOM_HOLD_STEP: number = 1.025;
const HOLD_DELAY_MS: number = 250;
const HOLD_TICK_MS: number = 50;

function toGrayHex(hex: string): string {
    const cleaned: string = hex.trim().replace('#', '');
    if (cleaned.length !== 3 && cleaned.length !== 6) {
        return hex;
    }
    const expanded: string =
        cleaned.length === 3
            ? cleaned
                  .split('')
                  .map((ch: string) => ch + ch)
                  .join('')
            : cleaned;
    const r: number = parseInt(expanded.slice(0, 2), 16);
    const g: number = parseInt(expanded.slice(2, 4), 16);
    const b: number = parseInt(expanded.slice(4, 6), 16);
    const lum: number = Math.round(0.2126 * r + 0.7152 * g + 0.0722 * b);
    const channel: string = lum.toString(16).padStart(2, '0');
    return `#${channel}${channel}${channel}`;
}

// The light engineering sheet when the page declares no sheet tokens: it repeats the
// `--sc-color-sheet-*` and `--sc-plan-*` values of the token sheet.
const FALLBACK_COLORS: IResolvedColors = {
    paper: '#e6e7ea',
    paper2: '#f4f4f6',
    paper3: '#d5d7dd',
    rule: '#7f8491',
    ink: '#15171d',
    ink2: '#2b2e37',
    ink3: '#474b57',
    ink4: '#5c606c',
    ink5: '#9a9ea9',
    warn: '#8a5a00',
    err: '#b3261e',
    step: '#fbfbfc',
    stepStroke: '#3d414c',
    winder: '#e9f0fa',
    landing: '#f7efdc',
    opening: '#b3261e',
    stairWidth: '#2d5b86',
    handrail: '#7c4f26',
    post: '#5e3b1c',
    baluster: '#7f5c3a',
    axis: '#b3261e',
};
const BEM_BLOCK: string = 'sc-plan-view';

@Component({
    selector: 'sc-plan-view',
    templateUrl: './plan-view.component.html',
    styleUrl: './plan-view.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // rt-tools
        BlockDirective,
        ElemDirective,
        ModDirective,
        RtButtonDirective,
    ],
    host: {
        class: BEM_BLOCK,
        '[attr.data-panes]': 'drawnPanes()',
    },
})
export class PlanViewComponent implements OnDestroy {
    readonly #document: Document = inject(DOCUMENT);
    #ctx: CanvasRenderingContext2D | null = null;
    #resizeObserver: ResizeObserver | null = null;
    #abortController: AbortController | null = null;
    #holdTimeout: ReturnType<typeof globalThis.setTimeout> | null = null;
    #holdInterval: ReturnType<typeof globalThis.setInterval> | null = null;
    #dpr: number = 1;
    #colors: IResolvedColors = FALLBACK_COLORS;
    // The last laid-out sheet: pan, zoom and resizing redraw it without laying it out again.
    #sheetCache: ISheetCacheEntry | null = null;
    // The canvas corner the controls take, at the largest size they have had: the fitted sheet
    // keeps clear of it and does not jump when the controls fold.
    #controlsCorner: { w: number; h: number } = { w: 0, h: 0 };

    protected readonly controlsVisible: WritableSignal<boolean> = signal<boolean>(true);
    /** The ids of the panes the last draw put on the canvas, space-separated — read by the end-to-end check. */
    protected readonly drawnPanes: WritableSignal<string> = signal<string>('');
    protected readonly controlsBar: Signal<ElementRef<HTMLElement> | undefined> = viewChild<ElementRef<HTMLElement>>('controlsBar');

    public readonly canvasRef: Signal<ElementRef<HTMLCanvasElement> | undefined> = viewChild<ElementRef<HTMLCanvasElement>>('planCanvas');

    public readonly result: InputSignal<IStairCalculationResult | null> = input<IStairCalculationResult | null>(null);
    public readonly config: InputSignal<IStairConfig | null> = input<IStairConfig | null>(null);
    public readonly hideControls: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
        transform: booleanAttribute,
    });
    public readonly monochrome: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
        transform: booleanAttribute,
    });
    /** The «Фасады» canvas position: the front and the side view on one floor line instead of the sheet. */
    public readonly sideOnly: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, {
        transform: booleanAttribute,
    });

    // Global view pan + zoom — applied to the whole canvas (all panes shift/zoom together).
    // Mouse wheel modifies viewZoom; right-button drag modifies viewPan; double-click resets.
    #viewPanX: number = 0;
    #viewPanY: number = 0;
    #viewZoom: number = 1;
    #isPanning: boolean = false;
    #panStartX: number = 0;
    #panStartY: number = 0;

    constructor() {
        afterNextRender(() => {
            const canvasEl: HTMLCanvasElement | undefined = this.canvasRef()?.nativeElement;
            if (!canvasEl) {
                return;
            }

            this.#ctx = canvasEl.getContext('2d');
            this.#dpr = globalThis.devicePixelRatio;
            this.#colors = this.#resolveColors(canvasEl);

            this.#resizeObserver = new ResizeObserver(() => {
                this.#resizeAndDraw();
            });
            this.#resizeObserver.observe(canvasEl.parentElement ?? canvasEl);

            this.#abortController = new AbortController();
            const signal: AbortSignal = this.#abortController.signal;

            canvasEl.addEventListener(
                'wheel',
                (event: WheelEvent) => {
                    event.preventDefault();
                    this.#handleWheel(event, canvasEl);
                },
                { passive: false, signal }
            );

            canvasEl.addEventListener(
                'dblclick',
                () => {
                    this.#viewZoom = 1;
                    this.#viewPanX = 0;
                    this.#viewPanY = 0;
                    this.#draw();
                },
                { signal }
            );

            canvasEl.addEventListener(
                'contextmenu',
                (event: MouseEvent) => {
                    event.preventDefault();
                },
                { signal }
            );

            canvasEl.addEventListener(
                'mousedown',
                (event: MouseEvent) => {
                    if (event.button === 2) {
                        this.#isPanning = true;
                        // Drag pans the GLOBAL view
                        this.#panStartX = event.clientX - this.#viewPanX;
                        this.#panStartY = event.clientY - this.#viewPanY;
                    }
                },
                { signal }
            );

            canvasEl.addEventListener(
                'mousemove',
                (event: MouseEvent) => {
                    if (!this.#isPanning) {
                        return;
                    }
                    this.#viewPanX = event.clientX - this.#panStartX;
                    this.#viewPanY = event.clientY - this.#panStartY;
                    this.#draw();
                },
                { signal }
            );

            canvasEl.addEventListener('mouseup', () => (this.#isPanning = false), { signal });
            canvasEl.addEventListener('mouseleave', () => (this.#isPanning = false), { signal });

            this.#resizeAndDraw();
        });

        effect(() => {
            this.result();
            this.config();
            this.monochrome();
            this.sideOnly();
            const canvasEl: HTMLCanvasElement | undefined = this.canvasRef()?.nativeElement;
            if (canvasEl && typeof globalThis.getComputedStyle === 'function') {
                this.#colors = this.#resolveColors(canvasEl);
            }
            this.#draw();
        });
    }

    public ngOnDestroy(): void {
        this.#resizeObserver?.disconnect();
        this.#abortController?.abort();
        this.#clearHold();
    }

    /**
     * The whole «План» sheet as a PNG, whatever the window and the view: the fixed sheet drawn
     * at twice its size on a canvas of its own. Null when there is nothing to draw.
     */
    public exportSheetPng(): Promise<Blob | null> {
        const result: IStairCalculationResult | null = this.result();
        const config: IStairConfig | null = this.config();
        const canvas: HTMLCanvasElement = this.#document.createElement('canvas');
        canvas.width = SHEET_SIZE.width * EXPORT_PIXEL_RATIO;
        canvas.height = SHEET_SIZE.height * EXPORT_PIXEL_RATIO;
        const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
        if (!result || !config || !ctx) {
            return Promise.resolve(null);
        }
        ctx.setTransform(EXPORT_PIXEL_RATIO, 0, 0, EXPORT_PIXEL_RATIO, 0, 0);
        ctx.fillStyle = this.#colors.paper;
        ctx.fillRect(0, 0, SHEET_SIZE.width, SHEET_SIZE.height);
        this.#paintSheet(ctx, planSheet(result, config, createCanvasTextMeasurer(ctx)));
        return new Promise<Blob | null>((resolve: (blob: Blob | null) => void) => {
            canvas.toBlob(resolve, 'image/png');
        });
    }

    public redraw(): void {
        this.#draw();
    }

    // ─── Public control buttons ───────────────────────────────

    protected startZoom(direction: number, event: PointerEvent): void {
        this.#capturePointer(event);
        this.#applyViewZoom(direction > 0 ? ZOOM_STEP : 1 / ZOOM_STEP);
        this.#scheduleHold(() => {
            this.#applyViewZoom(direction > 0 ? ZOOM_HOLD_STEP : 1 / ZOOM_HOLD_STEP);
        });
    }

    protected startPan(dx: number, dy: number, event: PointerEvent): void {
        this.#capturePointer(event);
        this.#applyViewPan(dx * PAN_STEP_PX, dy * PAN_STEP_PX);
        this.#scheduleHold(() => {
            this.#applyViewPan(dx * PAN_HOLD_STEP_PX, dy * PAN_HOLD_STEP_PX);
        });
    }

    protected stopHold(): void {
        this.#clearHold();
    }

    protected resetView(): void {
        this.#clearHold();
        this.#viewPanX = 0;
        this.#viewPanY = 0;
        this.#viewZoom = 1;
        this.#draw();
    }

    protected toggleControls(): void {
        this.#clearHold();
        this.controlsVisible.update((visible: boolean) => !visible);
    }

    #applyViewZoom(factor: number): void {
        const newZoom: number = Math.max(1, Math.min(MAX_VIEW_ZOOM, this.#viewZoom * factor));
        if (newZoom === this.#viewZoom) {
            return;
        }
        this.#viewZoom = newZoom;
        this.#draw();
    }

    #applyViewPan(dx: number, dy: number): void {
        this.#viewPanX += dx;
        this.#viewPanY += dy;
        this.#draw();
    }

    #capturePointer(event: PointerEvent): void {
        try {
            (event.target as HTMLElement).setPointerCapture(event.pointerId);
        } catch {
            // Synthetic event or no active pointer — hold still works via pointerup listener.
        }
    }

    #scheduleHold(tick: () => void): void {
        this.#clearHold();
        this.#holdTimeout = globalThis.setTimeout(() => {
            this.#holdTimeout = null;
            this.#holdInterval = globalThis.setInterval(tick, HOLD_TICK_MS);
        }, HOLD_DELAY_MS);
    }

    #clearHold(): void {
        if (this.#holdTimeout !== null) {
            globalThis.clearTimeout(this.#holdTimeout);
            this.#holdTimeout = null;
        }
        if (this.#holdInterval !== null) {
            globalThis.clearInterval(this.#holdInterval);
            this.#holdInterval = null;
        }
    }

    // ─── Color resolution ─────────────────────────────────────

    #resolveColors(canvasEl: HTMLElement): IResolvedColors {
        const styles: CSSStyleDeclaration = getComputedStyle(canvasEl);
        const v: (name: string, fallback: string) => string = (name: string, fallback: string): string =>
            styles.getPropertyValue(name).trim() || fallback;

        const base: IResolvedColors = {
            // The light sheet — the 3D ground — and its own dark ink, not the page's light ink.
            paper: v('--sc-color-sheet-ground', FALLBACK_COLORS.paper),
            paper2: v('--sc-color-sheet-plate', FALLBACK_COLORS.paper2),
            paper3: v('--sc-color-sheet-grid', FALLBACK_COLORS.paper3),
            rule: v('--sc-color-sheet-rule', FALLBACK_COLORS.rule),
            ink: v('--sc-color-sheet-ink', FALLBACK_COLORS.ink),
            ink2: v('--sc-color-sheet-ink-2', FALLBACK_COLORS.ink2),
            ink3: v('--sc-color-sheet-ink-3', FALLBACK_COLORS.ink3),
            ink4: v('--sc-color-sheet-ink-4', FALLBACK_COLORS.ink4),
            ink5: v('--sc-color-sheet-ink-5', FALLBACK_COLORS.ink5),
            warn: v('--sc-color-sheet-warn', FALLBACK_COLORS.warn),
            err: v('--sc-color-sheet-err', FALLBACK_COLORS.err),
            step: v('--sc-plan-step-fill', FALLBACK_COLORS.step),
            stepStroke: v('--sc-plan-step-stroke', FALLBACK_COLORS.stepStroke),
            winder: v('--sc-plan-winder-fill', FALLBACK_COLORS.winder),
            landing: v('--sc-plan-landing-fill', FALLBACK_COLORS.landing),
            opening: v('--sc-plan-opening', FALLBACK_COLORS.opening),
            stairWidth: v('--sc-plan-stair-width', FALLBACK_COLORS.stairWidth),
            handrail: v('--sc-plan-handrail', FALLBACK_COLORS.handrail),
            post: v('--sc-plan-post', FALLBACK_COLORS.post),
            baluster: v('--sc-plan-baluster', FALLBACK_COLORS.baluster),
            axis: v('--sc-plan-axis', FALLBACK_COLORS.axis),
        };

        if (!this.monochrome()) {
            return base;
        }

        return {
            ...base,
            // Keep paper/ink series (already neutral); desaturate semantic colors only.
            step: toGrayHex(base.step),
            stepStroke: toGrayHex(base.stepStroke),
            winder: toGrayHex(base.winder),
            landing: toGrayHex(base.landing),
            opening: toGrayHex(base.opening),
            stairWidth: toGrayHex(base.stairWidth),
            handrail: toGrayHex(base.handrail),
            post: toGrayHex(base.post),
            baluster: toGrayHex(base.baluster),
            axis: toGrayHex(base.axis),
            warn: toGrayHex(base.warn),
            err: toGrayHex(base.err),
        };
    }

    // ─── Resize + main draw entry ─────────────────────────────

    #resizeAndDraw(): void {
        const canvasEl: HTMLCanvasElement | undefined = this.canvasRef()?.nativeElement;
        if (!canvasEl) {
            return;
        }

        const parent: HTMLElement = canvasEl.parentElement ?? canvasEl;
        const w: number = parent.clientWidth;
        const h: number = parent.clientHeight;

        canvasEl.width = w * this.#dpr;
        canvasEl.height = h * this.#dpr;
        canvasEl.style.width = `${String(w)}px`;
        canvasEl.style.height = `${String(h)}px`;

        this.#draw();
    }

    #draw(): void {
        const ctx: CanvasRenderingContext2D | null = this.#ctx;
        const r: IStairCalculationResult | null = this.result();
        const cfg: IStairConfig | null = this.config();
        if (!ctx || !r || !cfg) {
            return;
        }

        const canvas: HTMLCanvasElement = ctx.canvas;
        const cw: number = canvas.width / this.#dpr;
        const ch: number = canvas.height / this.#dpr;

        // Reset transform; draw paper bg + grid in raw canvas coords.
        ctx.setTransform(this.#dpr, 0, 0, this.#dpr, 0, 0);
        this.#drawBackgroundGrid(ctx, cw, ch);

        // The sheet is laid out once on the fixed sheet; the canvas only shows it — fitted whole
        // and clear of the controls, then moved and zoomed by the user like a drawing viewer.
        const sheet: ISheet = this.#sheetFor(ctx, r, cfg);
        const fit: ISheetFit = fitSheet(sheet.frame, { width: cw, height: ch }, this.#controlsReserve(canvas, cw, ch));
        ctx.translate(this.#viewPanX, this.#viewPanY);
        ctx.scale(this.#viewZoom, this.#viewZoom);
        ctx.translate(fit.x, fit.y);
        ctx.scale(fit.scale, fit.scale);
        this.#paintSheet(ctx, sheet);
        this.drawnPanes.set(sheet.panes.map((pane: IPane) => pane.id).join(' '));
    }

    /** The frame and every pane of a sheet, in sheet pixels. */
    #paintSheet(ctx: CanvasRenderingContext2D, sheet: ISheet): void {
        this.#drawFrame(ctx, sheet.frame);
        for (const pane of sheet.panes) {
            this.#drawPane(ctx, pane);
        }
    }

    /** The sheet for this calculation and position, laid out once and reused while only the view or the canvas changes. */
    #sheetFor(ctx: CanvasRenderingContext2D, result: IStairCalculationResult, config: IStairConfig): ISheet {
        const side: boolean = this.sideOnly();
        const cached: ISheetCacheEntry | null = this.#sheetCache;
        if (cached?.result === result && cached.config === config && cached.side === side) {
            return cached.sheet;
        }
        const measurer: ITextMeasurer = createCanvasTextMeasurer(ctx);
        const sheet: ISheet = side ? elevationsSheet(result, config, measurer) : planSheet(result, config, measurer);
        this.#sheetCache = { result, config, side, sheet };
        return sheet;
    }

    /**
     * The bottom-right canvas corner under the zoom and pan controls, in canvas pixels; null when
     * the controls are not shown.
     */
    #controlsReserve(canvas: HTMLCanvasElement, width: number, height: number): IPaneRect | null {
        if (this.hideControls()) {
            return null;
        }
        const bar: HTMLElement | undefined = this.controlsBar()?.nativeElement;
        if (bar) {
            const canvasRect: DOMRect = canvas.getBoundingClientRect();
            const barRect: DOMRect = bar.getBoundingClientRect();
            this.#controlsCorner = {
                w: Math.max(this.#controlsCorner.w, Math.ceil(canvasRect.right - barRect.left) + CONTROLS_CLEARANCE),
                h: Math.max(this.#controlsCorner.h, Math.ceil(canvasRect.bottom - barRect.top) + CONTROLS_CLEARANCE),
            };
        }
        const { w, h } = this.#controlsCorner;
        return w > CONTROLS_CLEARANCE && h > CONTROLS_CLEARANCE ? { x: width - w, y: height - h, w, h } : null;
    }

    /** The one thin frame of the sheet. */
    #drawFrame(ctx: CanvasRenderingContext2D, frame: IPaneRect): void {
        ctx.save();
        ctx.strokeStyle = this.#colors.rule;
        ctx.lineWidth = 1;
        ctx.strokeRect(frame.x + 0.5, frame.y + 0.5, frame.w - 1, frame.h - 1);
        ctx.restore();
    }

    /** Paper background + adaptive pan/zoom-following grid, drawn in raw (pre-transform) canvas coords. */
    #drawBackgroundGrid(ctx: CanvasRenderingContext2D, cw: number, ch: number): void {
        const colors: IResolvedColors = this.#colors;
        ctx.fillStyle = colors.paper;
        ctx.fillRect(0, 0, cw, ch);

        // Grid spacing scales with viewZoom, then halves/doubles to keep density visually
        // consistent (~25–80px on screen); offset by (viewPan mod spacing) so cells follow pan.
        let gridSpacing: number = PANE_GRID_PX * this.#viewZoom;
        while (gridSpacing > 80) {
            gridSpacing /= 2;
        }
        while (gridSpacing < 25) {
            gridSpacing *= 2;
        }
        const offsetX: number = ((this.#viewPanX % gridSpacing) + gridSpacing) % gridSpacing;
        const offsetY: number = ((this.#viewPanY % gridSpacing) + gridSpacing) % gridSpacing;

        ctx.strokeStyle = colors.paper3;
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (let x: number = offsetX; x < cw; x += gridSpacing) {
            ctx.moveTo(x + 0.5, 0);
            ctx.lineTo(x + 0.5, ch);
        }
        for (let y: number = offsetY; y < ch; y += gridSpacing) {
            ctx.moveTo(0, y + 0.5);
            ctx.lineTo(cw, y + 0.5);
        }
        ctx.stroke();
    }

    // ─── Pane: content clipped to the pane, title row, detail cell frame ────

    #drawPane(ctx: CanvasRenderingContext2D, pane: IPane): void {
        const colors: IResolvedColors = this.#colors;
        const rect: IPaneRect = pane.rect;

        ctx.save();
        ctx.beginPath();
        ctx.rect(rect.x, rect.y, rect.w, rect.h);
        ctx.clip();

        // Pane content (paper bg + grid are drawn globally in #draw)
        drawPlanPrimitives(ctx, pane.projection.primitives, rect, colors);

        // Title row: the view name, then its scale — the same on every view of the sheet.
        const titleY: number = rect.y + SHEET_TITLE_BAND / 2;
        const titleX: number = rect.x + (pane.role === EPaneRole.Detail ? 8 : 0);
        const title: string = pane.title.toUpperCase();
        ctx.font = `${String(SHEET_TITLE_FONT.weight)} ${String(SHEET_TITLE_FONT.size)}px ${SHEET_TITLE_FONT.family}`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = colors.ink2;
        ctx.fillText(title, titleX, titleY);
        const titleW: number = ctx.measureText(title).width;
        if (pane.scale !== null) {
            ctx.fillStyle = colors.ink3;
            ctx.fillText(pane.scale, titleX + titleW + TITLE_SCALE_GAP, titleY);
        }
        // The title is underlined, as a view name is on a drawing.
        ctx.strokeStyle = colors.ink3;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(titleX, titleY + SHEET_TITLE_FONT.size / 2 + 3.5);
        ctx.lineTo(titleX + titleW, titleY + SHEET_TITLE_FONT.size / 2 + 3.5);
        ctx.stroke();

        ctx.restore();

        // A detail stands in a cell of the grid; the cell is framed by a thin rule.
        if (pane.role === EPaneRole.Detail) {
            ctx.save();
            ctx.strokeStyle = colors.rule;
            ctx.lineWidth = 1;
            ctx.strokeRect(rect.x + 0.5, rect.y + 0.5, rect.w - 1, rect.h - 1);
            ctx.restore();
        }
    }

    // ─── Pan/zoom helpers ─────────────────────────────────────

    #handleWheel(event: WheelEvent, canvasEl: HTMLCanvasElement): void {
        const rect: DOMRect = canvasEl.getBoundingClientRect();
        const mxCanvas: number = event.clientX - rect.left;
        const myCanvas: number = event.clientY - rect.top;

        // World coords under the mouse before zoom change (in pre-transform canvas space)
        const worldX: number = (mxCanvas - this.#viewPanX) / this.#viewZoom;
        const worldY: number = (myCanvas - this.#viewPanY) / this.#viewZoom;

        // Smooth, delta-proportional zoom — each notch scrolls a small step, trackpad pinches feel natural.
        // exp(-delta/sensitivity) keeps zoom changes smooth across mouse wheels and trackpads.
        const sensitivity: number = 250;
        const factor: number = Math.exp(-event.deltaY / sensitivity);
        const newZoom: number = Math.max(1, Math.min(MAX_VIEW_ZOOM, this.#viewZoom * factor));
        if (newZoom === this.#viewZoom) {
            return;
        }

        // Keep the same world point under the cursor after zoom change
        this.#viewPanX = mxCanvas - worldX * newZoom;
        this.#viewPanY = myCanvas - worldY * newZoom;
        this.#viewZoom = newZoom;
        this.#draw();
    }
}
